import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  Heading,
  useToast,
  ButtonGroup,
  VStack,
  SimpleGrid,
} from '@chakra-ui/react';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useCallback, useEffect } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import axios from 'axios';
import { MaskedInput } from '../../../../../../../../../../../../../components/Form/MaskedInput';
import { translateError } from '../../../../../../../../../../../../../utils/errors';
import { useVenture } from '../../../../../../../../../../../../../hooks/venture';
import { NumberInput } from '../../../../../../../../../../../../../components/Form/NumberInput';
import { IVentureExperienceLevelBase } from '../../../../../../../../../../../../../models/ventureExperienceLevels';
import { createVentureExperienceLevelsService } from '../../../../../../../../../../../../../services/Ventures/CreateVentureExperienceLevelsService';
import { updateVentureExperienceLevelsService } from '../../../../../../../../../../../../../services/Ventures/UpdateVentureExperienceLevelsService';

interface IHandleVentureExperienceLevelModalFormData {
  title: string;
  level: number;
  minAllowedBookingExperience: number;
  maxAllowedBookingExperience: number;
}

interface IHandleVentureExperienceLevelModalProps {
  ventureExperienceLevel?: IVentureExperienceLevelBase;
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
}

const userCategoryFormSchema = Yup.object().shape({
  title: Yup.string()
    .nullable()
    .required('Requerido')
    .transform((value, originalValue) => (originalValue === '' ? null : value)),
  level: Yup.number()
    .integer('Valores inteiros')
    .min(0, 'Valores positivos')
    .nullable()
    .required('Requerido')
    .transform((value, originalValue) => (originalValue === '' ? null : value)),
  minAllowedBookingExperience: Yup.number()
    .integer('Valores inteiros')
    .min(0, 'Valores positivos')
    .nullable()
    .required('Requerido')
    .transform((value, originalValue) => (originalValue === '' ? null : value)),
  maxAllowedBookingExperience: Yup.number()
    .integer('Valores inteiros')
    .min(0, 'Valores positivos')
    .nullable()
    .required('Requerido')
    .transform((value, originalValue) => (originalValue === '' ? null : value)),
});

export const HandleVentureExperienceLevelModal = ({
  ventureExperienceLevel,
  isOpen,
  onClose,
  onSave,
}: IHandleVentureExperienceLevelModalProps): JSX.Element => {
  const toast = useToast();

  const { venture } = useVenture();

  const { register, formState, handleSubmit, reset } = useForm({
    resolver: yupResolver(userCategoryFormSchema),
  });

  const { errors } = formState;

  useEffect(() => {
    reset({
      title: ventureExperienceLevel?.title,
      level: ventureExperienceLevel?.level,
      minAllowedBookingExperience:
        ventureExperienceLevel?.minAllowedBookingExperience,
      maxAllowedBookingExperience:
        ventureExperienceLevel?.maxAllowedBookingExperience,
    });
  }, [ventureExperienceLevel, reset]);

  const handleVentureModalitySubmit: SubmitHandler<IHandleVentureExperienceLevelModalFormData> =
    useCallback(
      async ({
        title,
        level,
        minAllowedBookingExperience,
        maxAllowedBookingExperience,
      }) => {
        if (venture) {
          if (!ventureExperienceLevel) {
            try {
              await createVentureExperienceLevelsService({
                maxAllowedBookingExperience,
                minAllowedBookingExperience,
                title,
                level,
                ventureId: venture.id,
              });

              reset({
                maxAllowedBookingExperience: null,
                minAllowedBookingExperience: null,
                title: null,
                level: null,
              });

              onSave();

              toast({
                title: 'Cadastrado com sucesso',
                description: 'Nível de experiência cadastrado corretamente.',
                status: 'success',
                duration: 3000,
                isClosable: true,
                variant: 'subtle',
                position: 'top-right',
              });
            } catch (err) {
              if (axios.isAxiosError(err) && err.response?.status !== 401) {
                toast({
                  title: 'Falha ao cadastrar',
                  description:
                    translateError({ message: err.response?.data.message }) ||
                    'Ocorreu um erro ao cadastrar o nível de experiência, tente novamente.',
                  status: 'error',
                  duration: 3000,
                  isClosable: true,
                  variant: 'subtle',
                  position: 'top-right',
                });
              }
            }
          } else {
            try {
              await updateVentureExperienceLevelsService({
                maxAllowedBookingExperience,
                minAllowedBookingExperience,
                title,
                level,
                experienceLevelId: ventureExperienceLevel.id,
              });

              reset({
                maxAllowedBookingExperience: null,
                minAllowedBookingExperience: null,
                title: null,
                level: null,
              });

              onSave();

              toast({
                title: 'Atualizado com sucesso',
                description: 'Nível de experiência atualizado corretamente.',
                status: 'success',
                duration: 3000,
                isClosable: true,
                variant: 'subtle',
                position: 'top-right',
              });
            } catch (err) {
              if (axios.isAxiosError(err) && err.response?.status !== 401) {
                toast({
                  title: 'Falha ao atualizar',
                  description:
                    translateError({ message: err.response?.data.message }) ||
                    'Ocorreu um erro ao atualizar o nível de experiência, tente novamente.',
                  status: 'error',
                  duration: 3000,
                  isClosable: true,
                  variant: 'subtle',
                  position: 'top-right',
                });
              }
            }
          }
        }
      },
      [onSave, reset, toast, venture, ventureExperienceLevel],
    );

  const handleCloseModal = useCallback(() => {
    reset({
      maxAllowedBookingExperience: null,
      minAllowedBookingExperience: null,
      title: null,
      level: null,
    });

    onClose();
  }, [onClose, reset]);

  return (
    <Modal size="2xl" isOpen={isOpen} onClose={handleCloseModal}>
      <ModalOverlay />

      <ModalContent
        as="form"
        onSubmit={handleSubmit(handleVentureModalitySubmit)}
      >
        <ModalHeader>
          <Heading size="lg" fontWeight="normal">
            Nível de experiência
          </Heading>
        </ModalHeader>

        <ModalCloseButton />

        <ModalBody>
          <VStack spacing="8">
            <MaskedInput
              label="Título"
              error={errors.title}
              {...register('title')}
            />

            <SimpleGrid minChildWidth="240px" spacing="6" w="100%">
              <NumberInput
                label="Nível"
                error={errors.level}
                {...register('level')}
              />

              <NumberInput
                label="Nível mínimo de evento permitido"
                error={errors.minAllowedBookingExperience}
                {...register('minAllowedBookingExperience')}
              />

              <NumberInput
                label="Nível máximo de evento permitido"
                error={errors.maxAllowedBookingExperience}
                {...register('maxAllowedBookingExperience')}
              />
            </SimpleGrid>
          </VStack>
        </ModalBody>

        <ModalFooter>
          <ButtonGroup>
            <Button colorScheme="blackAlpha" onClick={handleCloseModal}>
              Cancelar
            </Button>

            <Button
              colorScheme="green"
              type="submit"
              isLoading={formState.isSubmitting}
            >
              Salvar
            </Button>
          </ButtonGroup>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
